import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const NAV_ITEMS = [
  { to: '/', label: 'Home', active: true },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/archive', label: 'Archive' },
  { to: '/add-to-homescreen', label: 'Add to Home Screen' },
  { to: '/admin/login', label: 'Admin' },
];

const FOOTER_LEGAL_LINKS = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/shipping', label: 'Shipping' },
  { to: '/faq', label: 'FAQ' },
];

function PublicHome() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('latest');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get('/public/posts');
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }).toUpperCase();
  };

  const stripMarkdown = (content) => {
    let text = content.replace(/!\[.*?\]\(.*?\)/g, '');
    text = text.replace(/\[(.*?)\]\(.*?\)/g, '$1');
    text = text.replace(/\*\*?(.*?)\*\*?/g, '$1');
    text = text.replace(/__?(.*?)__?/g, '$1');
    text = text.replace(/^#{1,6}\s+/gm, '');
    text = text.replace(/^-{3,}$/gm, '');
    text = text.replace(/\n{3,}/g, '\n\n');
    return text.trim();
  };

  const getExcerpt = (content, length = 120) => {
    const plainText = stripMarkdown(content);
    if (plainText.length <= length) return plainText;
    return plainText.substring(0, length).trim() + '...';
  };

  const getMainImage = (post) => {
    if (post.image_urls && post.image_urls.length > 0) {
      return post.image_urls[0];
    }
    return post.image_url;
  };

  const featuredPost = posts[0];
  const otherPosts = posts.slice(1);

  // "Latest" keeps the API's newest-first order as-is; "Top"/"Discussions"
  // re-sort that same set by likes/comments rather than fetching separately —
  // the featured hero post above always stays the newest post regardless of tab.
  const sortedOtherPosts = useMemo(() => {
    if (activeTab === 'top') {
      return [...otherPosts].sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
    }
    if (activeTab === 'discussions') {
      return [...otherPosts].sort((a, b) => (b.comment_count || 0) - (a.comment_count || 0));
    }
    return otherPosts;
  }, [activeTab, otherPosts]);

  // On the Discussions tab, clicking a post should land you on its comments,
  // not the top of the article — elsewhere, link to the post as normal.
  const postHref = (post) => activeTab === 'discussions'
    ? `/post/${post.slug}#comments`
    : `/post/${post.slug}`;

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} />

      {/* Hero Section */}
      {featuredPost && (
        <section className="substack-hero">
          <div className="hero-container">
            <Link 
              to={`/post/${featuredPost.slug}`} 
              className="hero-image-link"
              onContextMenu={(e) => e.preventDefault()}
            >
              <div className="hero-featured-image">
                {getMainImage(featuredPost) ? (
                  <img 
                    src={getMainImage(featuredPost)} 
                    alt={featuredPost.title}
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                  />
                ) : (
                  <div className="hero-placeholder">
                    <span>Featured Image</span>
                  </div>
                )}
              </div>
            </Link>
            <div className="hero-info">
              <Link to={`/post/${featuredPost.slug}`} className="hero-title-link">
                <h1 className="hero-post-title">{featuredPost.title}</h1>
              </Link>
              <p className="hero-excerpt">{getExcerpt(featuredPost.content, 150)}</p>
              <div className="hero-meta">
                <span className="hero-date">{formatDate(featuredPost.published_at)}</span>
                <span className="hero-separator">|</span>
                <span className="hero-author">the OBX BEACH BUM</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <main className="substack-main">
        <div className="main-container">
          {/* Tabs */}
          <div className="content-tabs">
            <button 
              className={`tab-btn ${activeTab === 'latest' ? 'active' : ''}`}
              onClick={() => setActiveTab('latest')}
            >
              Latest
            </button>
            <button 
              className={`tab-btn ${activeTab === 'top' ? 'active' : ''}`}
              onClick={() => setActiveTab('top')}
            >
              Top
            </button>
            <button 
              className={`tab-btn ${activeTab === 'discussions' ? 'active' : ''}`}
              onClick={() => setActiveTab('discussions')}
            >
              Discussions
            </button>
          </div>

          <div className="content-layout">
            {/* Posts List */}
            <div className="posts-list">
              {loading ? (
                <div className="loading-spinner"></div>
              ) : sortedOtherPosts.length === 0 ? (
                <div className="empty-state">
                  <p>More posts coming soon...</p>
                </div>
              ) : (
                sortedOtherPosts.map((post) => (
                  <article key={post.id} className="post-item">
                    <div className="post-item-content">
                      <Link to={postHref(post)} className="post-item-title-link">
                        <h3 className="post-item-title">{post.title}</h3>
                      </Link>
                      <p className="post-item-subtitle">{getExcerpt(post.content, 80)}</p>
                      <div className="post-item-meta">
                        <span className="post-item-date">{formatDate(post.published_at)}</span>
                        <span className="post-item-author">the OBX BEACH BUM</span>
                        {activeTab === 'top' && post.likes_count > 0 && (
                          <span className="post-item-count">&hearts; {post.likes_count}</span>
                        )}
                        {activeTab === 'discussions' && (
                          <Link to={postHref(post)} className="post-item-count">
                            &#128172; {post.comment_count > 0 ? post.comment_count : 'Be the first to comment'}
                          </Link>
                        )}
                      </div>
                    </div>
                    {getMainImage(post) && (
                      <Link
                        to={postHref(post)}
                        className="post-item-image-link"
                        onContextMenu={(e) => e.preventDefault()}
                      >
                        <img 
                          src={getMainImage(post)} 
                          alt={post.title}
                          className="post-item-image"
                          draggable="false"
                          onContextMenu={(e) => e.preventDefault()}
                          onDragStart={(e) => e.preventDefault()}
                        />
                      </Link>
                    )}
                  </article>
                ))
              )}

              {sortedOtherPosts.length > 0 && (
                <Link to="/archive" className="see-all-btn">
                  See All →
                </Link>
              )}
            </div>

            {/* Sidebar */}
            <aside className="sidebar">
              <div className="sidebar-card">
                <h3 className="sidebar-title">the OBX Beach Bum</h3>
                <img 
                  src="https://daily-obx-cdn.b-cdn.net/newsletter-images/menher.jpg"
                  alt="the OBX Beach Bum"
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    marginBottom: '15px'
                  }}
                />
                <p className="sidebar-description">
                  I wander around the Outer Banks with my girlfriend and pair of cameras to photograph some of the most beautiful and stunning beachscapes and scenery anywhere. I'll share my photos, inspirational beach bum philosophy, location guides, and maybe even tutorials on how to get the most from your photography.
                </p>
                <div className="sidebar-subscription">
                  <p className="subscription-text">
                    My photos and writing are free. Always have been, always will be. If you want a daily OBX photo in your inbox or just want to support my work, there's an option for that. No pressure, no paywall.
                  </p>
                  <Link to="/subscribe" className="sidebar-subscribe-btn">
                    Follow
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer links={FOOTER_LINKS} legalLinks={FOOTER_LEGAL_LINKS} />
    </div>
  );
}

export default PublicHome;
